/**
 * Tibbs's pace at playback. Carl (Cartesia sonic-3.6) barely follows the speed
 * setting: about 155 words a minute at 1.0 and 162 at the maximum, 1.5
 * (measured 11-04-2026). Speech itself runs near 190 words a minute; the rest
 * is the pause he leaves at every full stop, 0.3 s on average and up to 0.65 s,
 * nearly a quarter of a readout. So each decoded chunk has its long silences
 * shortened to PAUSE_KEEP_S before it is scheduled: the words keep their pitch
 * and timing, only the gaps shrink, which brings a readout to about 165 to 180
 * words a minute. Pure: works on plain sample arrays, so it is tested without
 * Web Audio.
 */

/** The longest silence left in place (seconds). Half is kept at each end of a cut gap, so breaths and decays stay. */
export const PAUSE_KEEP_S = 0.14;
/** A 10 ms block is silent when its peak is under this (about −40 dBFS). */
export const SILENCE_PEAK = 0.01;
const BLOCK_S = 0.01;

/**
 * The channels with every silent run longer than `keepS` cut down to `keepS`
 * (the first and last halves kept, the middle dropped). Cuts fall only inside
 * silent blocks, so there is nothing to click. Returns the input untouched
 * when there is nothing to cut.
 */
export function tightenPauses(channels: Float32Array[], sampleRate: number, { keepS = PAUSE_KEEP_S, peak = SILENCE_PEAK } = {}): Float32Array[] {
  const len = channels[0]?.length ?? 0;
  const block = Math.max(1, Math.round(sampleRate * BLOCK_S));
  const blocks = Math.floor(len / block);
  if (!len || blocks < 2) return channels;
  const silent = new Uint8Array(blocks);
  for (let b = 0; b < blocks; b++) {
    let max = 0;
    for (const ch of channels) {
      for (let i = b * block, end = i + block; i < end; i++) {
        const v = Math.abs(ch[i]!);
        if (v > max) max = v;
      }
    }
    silent[b] = max < peak ? 1 : 0;
  }
  const keepBlocks = Math.max(1, Math.round(keepS / BLOCK_S));
  const head = Math.floor(keepBlocks / 2);
  // Spans of blocks to keep, [from, to).
  const spans: [number, number][] = [];
  let b = 0;
  while (b < blocks) {
    let e = b;
    if (!silent[b]) {
      while (e < blocks && !silent[e]) e++;
      spans.push([b, e]);
    } else {
      while (e < blocks && silent[e]) e++;
      if (e - b <= keepBlocks) spans.push([b, e]);
      else {
        spans.push([b, b + head]);
        spans.push([e - (keepBlocks - head), e]);
      }
    }
    b = e;
  }
  const kept = spans.reduce((n, [f, t]) => n + (t - f), 0);
  if (kept === blocks) return channels;
  const tail = len - blocks * block;
  return channels.map((ch) => {
    const out = new Float32Array(kept * block + tail);
    let o = 0;
    for (const [f, t] of spans) {
      out.set(ch.subarray(f * block, t * block), o);
      o += (t - f) * block;
    }
    out.set(ch.subarray(blocks * block), o);
    return out;
  });
}

/** An AudioBuffer with its long pauses shortened (the same buffer when nothing changes). */
export function tightenBuffer(c: BaseAudioContext, buf: AudioBuffer): AudioBuffer {
  const chans = Array.from({ length: buf.numberOfChannels }, (_, i) => buf.getChannelData(i));
  const out = tightenPauses(chans, buf.sampleRate);
  if (out === chans) return buf;
  const nb = c.createBuffer(out.length, out[0]!.length, buf.sampleRate);
  out.forEach((d, i) => nb.copyToChannel(d as Float32Array<ArrayBuffer>, i));
  return nb;
}
